import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { format, formatDistanceToNow } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import {
  Archive,
  ArchiveRestore,
  Briefcase,
  Building,
  Camera,
  Globe,
  Hash,
  MapPin,
  Inbox,
  Check as CheckIcon,
  ChevronDown,
  ChevronUp,
  Loader2,
  Mail,
  Phone,
  Plus,
  Send,
  Tag,
  Trash2,
  User,
  UserCircle,
  X as XIcon,
  MessageSquare,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useAddContactNote,
  useArchiveContact,
  useContact,
  useAcceptAllSuggestions,
  useContactSuggestions,
  useDecideSuggestion,
  useDeleteAvatar,
  useDeleteContact,
  useRestoreContact,
  useUpdateContact,
  useUploadAvatar,
} from '@/api/contacts'
import {
  useContactTasks,
  useCreateContactTask,
  useDeleteContactTask,
  useToggleContactTask,
  type CallTask,
} from '@/api/tasks'
import type { ContactStatus } from '@/types/contacts'
import { ProprietesContact } from './ProprietesContact'
import { ValeurProposee } from './ValeurProposee'
import { useSSE } from '@/contexts/SSEContext'
import { useQueryClient } from '@tanstack/react-query'
import { useProfile } from '@/api/profile'

interface Props {
  phoneNumber: string
  darkMode?: boolean
  onClose?: () => void
  onCall?: (number: string) => void
  /** Carnet Contacts : bouton « Conversation » à côté d'« Appeler ». */
  onOpenConversation?: () => void
  /** Contact tout juste créé : on ouvre l'édition sur le premier champ vide. */
  editerPremierVide?: boolean
  onEditionLancee?: () => void
}

type TabType = 'info' | 'tasks' | 'notes'

const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_AVATAR_SIZE = 5 * 1024 * 1024

function formatPhone(phone: string) {
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

export function ContactProfile({
  phoneNumber,
  darkMode = false,
  onCall,
  onOpenConversation,
  onClose,
  editerPremierVide = false,
  onEditionLancee,
}: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'en' ? enUS : fr

  const contactQuery = useContact(phoneNumber, 5)
  const contact = contactQuery.data

  const updateContact = useUpdateContact(phoneNumber)
  // Valeurs entendues dans les appels, à approuver (services/suggestions_fiche.py).
  const { data: suggestions = [] } = useContactSuggestions(phoneNumber)
  const decider = useDecideSuggestion(phoneNumber)
  const toutApprouver = useAcceptAllSuggestions(phoneNumber)
  // Fiche ouverte quand l'analyse de l'appel se termine : on relit les propositions.
  const { onContactSuggestions } = useSSE()
  const queryClient = useQueryClient()
  useEffect(
    () =>
      onContactSuggestions((e) => {
        const chiffres = (n: string) => n.replace(/\D/g, '')
        if (chiffres(e.phone_number) === chiffres(phoneNumber)) {
          queryClient.invalidateQueries({ queryKey: ['contact-suggestions', phoneNumber] })
        }
      }),
    [onContactSuggestions, phoneNumber, queryClient],
  )
  const proposition = (champ: string, affichage?: (v: string) => string) => {
    const s = suggestions.find((x) => x.field === champ)
    if (!s) return undefined
    const texte = String(s.value)
    return (
      <ValeurProposee
        texte={affichage ? affichage(texte) : texte}
        citation={s.quote}
        darkMode={darkMode}
        enCours={decider.isPending}
        onApprouver={() =>
          decider.mutate({ id: s.id, accept: true }, { onError: () => toast.error(t('sidebar.toasts.error', 'Erreur')) })
        }
        onRefuser={() =>
          decider.mutate({ id: s.id, accept: false }, { onError: () => toast.error(t('sidebar.toasts.error', 'Erreur')) })
        }
      />
    )
  }
  // Créer ou supprimer une propriété : réservé au gestionnaire (le serveur le vérifie aussi).
  const { data: profil } = useProfile()
  const peutGererProprietes = profil?.role === 'MANAGER'
  const addNote = useAddContactNote(phoneNumber)
  const archive = useArchiveContact(phoneNumber)
  const restore = useRestoreContact(phoneNumber)
  // Deleting (trash for 30 days) is for managers only, like the server.
  const supprimer = useDeleteContact(phoneNumber)
  const uploadAvatar = useUploadAvatar(phoneNumber)
  const deleteAvatar = useDeleteAvatar(phoneNumber)

  const tasksQuery = useContactTasks(phoneNumber)
  const tasks = tasksQuery.data || []
  const createTask = useCreateContactTask(phoneNumber)
  const toggleTask = useToggleContactTask(phoneNumber)
  const deleteTask = useDeleteContactTask(phoneNumber)

  const [activeTab, setActiveTab] = useState<TabType>('info')
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [noteContent, setNoteContent] = useState('')
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [showCompletedTasks, setShowCompletedTasks] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const avatarInputRef = useRef<HTMLInputElement>(null)
  const editInputRef = useRef<HTMLInputElement>(null)

  // Reset au changement de contact
  useEffect(() => {
    setActiveTab('info')
    setEditingField(null)
    setEditValue('')
    setNoteContent('')
    setNewTaskTitle('')
    setShowCompletedTasks(false)
    setShowArchiveConfirm(false)
    setShowDeleteConfirm(false)
  }, [phoneNumber])

  const startEditing = (field: string, currentValue: string) => {
    setEditingField(field)
    setEditValue(currentValue)
    setTimeout(() => editInputRef.current?.focus(), 0)
  }

  // Après « Nouveau contact », le formulaire ne demande que l'essentiel : on
  // enchaîne ici, curseur sur le premier champ vide de la fiche.
  useEffect(() => {
    if (!editerPremierVide || !contact?.id) return
    const ordre = ['first_name', 'last_name', 'email', 'company_name', 'job_title', 'secondary_phone', 'address', 'website'] as const
    const vide = ordre.find((f) => !((contact as unknown as Record<string, unknown>)[f]))
    setActiveTab('info')
    if (vide) startEditing(vide, '')
    onEditionLancee?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editerPremierVide, contact?.id])

  const saveField = async (field: string, value: string) => {
    setEditingField(null)
    // Étiquettes : saisies « VIP, garage », enregistrées en liste.
    if (field === 'tags') {
      const tags = [...new Set(value.split(',').map((x) => x.trim()).filter(Boolean))]
      if (tags.join(', ') === (contact?.tags || []).join(', ')) return
      try {
        await updateContact.mutateAsync({ tags })
      } catch {
        toast.error(t('sidebar.toasts.error', 'Erreur'))
      }
      return
    }
    const currentVal =
      ((contact || {}) as Record<string, unknown>)[field] as string | undefined
    if (value.trim() === (currentVal || '')) return
    try {
      await updateContact.mutateAsync({ [field]: value.trim() })
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code
      toast.error(
        code === 'invalid_secondary_phone'
          ? t('contacts.invalidSecondaryPhone', 'Ce numéro n’est pas valide.')
          : t('sidebar.toasts.error', 'Erreur'),
      )
    }
  }

  const handleEditKeyDown = (e: KeyboardEvent<HTMLInputElement>, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      void saveField(field, editValue)
    }
    if (e.key === 'Escape') setEditingField(null)
  }

  const handleAddNote = async () => {
    if (!noteContent.trim()) return
    try {
      await addNote.mutateAsync(noteContent.trim())
      setNoteContent('')
    } catch {
      toast.error(
        t('contacts.addNoteError', "Erreur lors de l'ajout de la note"),
      )
    }
  }

  const handleNoteKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleAddNote()
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      toast.error(t('toasts.avatar.formatNotSupported'))
      return
    }
    if (file.size > MAX_AVATAR_SIZE) {
      toast.error(t('toasts.avatar.fileTooLarge'))
      return
    }
    try {
      await uploadAvatar.mutateAsync(file)
    } catch {
      toast.error(t('toasts.common.uploadError'))
    } finally {
      if (avatarInputRef.current) avatarInputRef.current.value = ''
    }
  }

  const handleAvatarDelete = async () => {
    try {
      await deleteAvatar.mutateAsync()
    } catch {
      toast.error(t('sidebar.toasts.error', 'Erreur'))
    }
  }

  const handleArchive = async () => {
    try {
      await archive.mutateAsync()
      setShowArchiveConfirm(false)
    } catch {
      toast.error(t('sidebar.toasts.error', 'Erreur'))
    }
  }

  const handleDelete = async () => {
    try {
      await supprimer.mutateAsync()
      setShowDeleteConfirm(false)
      toast.success(t('contacts.delete.success', 'Contact mis à la corbeille'), {
        action: {
          label: t('contacts.delete.undo', 'Annuler'),
          onClick: () => {
            void api
              .post('/api/contacts/bulk/', { phones: [phoneNumber], action: 'undelete' })
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ['contacts'] })
                queryClient.invalidateQueries({ queryKey: ['contact', phoneNumber] })
                queryClient.invalidateQueries({ queryKey: ['inbox'] })
              })
              .catch(() => toast.error(t('sidebar.toasts.error', 'Erreur')))
          },
        },
      })
      onClose?.()
    } catch {
      toast.error(t('sidebar.toasts.error', 'Erreur'))
    }
  }

  const handleRestore = async () => {
    try {
      await restore.mutateAsync()
    } catch {
      toast.error(t('sidebar.toasts.error', 'Erreur'))
    }
  }

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return
    try {
      await createTask.mutateAsync(newTaskTitle.trim())
      setNewTaskTitle('')
    } catch {
      toast.error(t('sidebar.toasts.error', 'Erreur'))
    }
  }

  const pendingTasks = tasks.filter((task) => !task.is_completed)
  const completedTasks = tasks.filter((task) => task.is_completed)

  const statusConfig: Record<ContactStatus, { label: string; color: string }> = {
    prospect: {
      label: t('contacts.statuses.prospect', 'Prospect'),
      color: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    },
    lead: {
      label: t('contacts.statuses.lead', 'Lead'),
      color: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    },
    client: {
      label: t('contacts.statuses.client', 'Client'),
      color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    },
    inactive: {
      label: t('contacts.statuses.inactive', 'Inactif'),
      color: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    },
  }

  if (contactQuery.isLoading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center h-full',
          darkMode ? 'text-slate-500' : 'text-gray-400',
        )}
      >
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  const status = statusConfig[(contact?.status || 'prospect') as ContactStatus]
  const lastInteractionLabel = contact?.last_interaction
    ? formatDistanceToNow(new Date(contact.last_interaction), {
        addSuffix: false,
        locale: dateLocale,
      })
    : '—'
  const isUploadingAvatar = uploadAvatar.isPending || deleteAvatar.isPending

  return (
    <div
      className={cn(
        'w-full h-full flex flex-col',
        darkMode ? 'bg-slate-950' : 'bg-white',
      )}
    >
      {/* 1. Header — Avatar + Nom + Statut + Boutons */}
      <div
        className={cn(
          'relative flex flex-col items-center pt-5 pb-4 px-4 border-b',
          darkMode ? 'border-slate-800/50' : 'border-gray-100',
        )}
      >
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Fermer')}
            className={cn(
              'absolute top-3 right-3 p-1.5 rounded-lg transition-colors',
              darkMode ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100',
            )}
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleAvatarUpload}
        />
        <div
          className="relative group mb-3 cursor-pointer"
          onClick={() => avatarInputRef.current?.click()}
        >
          <Avatar
            className={cn(
              'h-20 w-20 border-2 border-transparent transition-colors',
              'group-hover:border-indigo-500/30',
            )}
          >
            {contact?.avatar_signed_url && (
              <AvatarImage
                src={contact.avatar_signed_url}
                alt={contact?.full_name || ''}
              />
            )}
            <AvatarFallback className={darkMode ? 'bg-slate-800' : 'bg-gray-200'}>
              <User
                className={cn(
                  'w-9 h-9',
                  darkMode ? 'text-slate-500' : 'text-gray-400',
                )}
              />
            </AvatarFallback>
          </Avatar>
          <div
            className={cn(
              'absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center border-2 transition-colors',
              darkMode
                ? 'bg-slate-700 border-slate-950 group-hover:bg-slate-600'
                : 'bg-gray-200 border-white group-hover:bg-gray-300',
            )}
          >
            {isUploadingAvatar ? (
              <Loader2
                className={cn(
                  'w-2.5 h-2.5 animate-spin',
                  darkMode ? 'text-white' : 'text-gray-600',
                )}
              />
            ) : (
              <Camera
                className={cn(
                  'w-2.5 h-2.5',
                  darkMode ? 'text-white' : 'text-gray-600',
                )}
              />
            )}
          </div>
          {contact?.avatar_signed_url && !isUploadingAvatar && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                void handleAvatarDelete()
              }}
              className={cn(
                'absolute -top-1 -left-1 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity',
                darkMode
                  ? 'bg-red-600 hover:bg-red-500'
                  : 'bg-red-500 hover:bg-red-600',
              )}
            >
              <XIcon className="w-2.5 h-2.5 text-white" />
            </button>
          )}
        </div>
        <h3
          className={cn(
            'text-base font-bold tracking-tight',
            darkMode ? 'text-white' : 'text-gray-900',
          )}
        >
          {contact?.full_name || phoneNumber}
        </h3>
        <div className="flex items-center gap-2 mt-1">
          <span
            className={cn(
              'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border',
              status.color,
            )}
          >
            {status.label}
          </span>
          {(contact?.company_name || contact?.job_title) && (
            <span
              className={cn(
                'text-xs',
                darkMode ? 'text-slate-500' : 'text-gray-400',
              )}
            >
              {[contact?.company_name, contact?.job_title]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </div>
        {contact?.is_archived && (
          <div className="flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30">
            <Archive className="w-3 h-3 text-amber-600 dark:text-amber-400" />
            <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400">
              {t('contacts.archive.badge', 'Archivé')}
            </span>
          </div>
        )}

        {(onCall || onOpenConversation) && (
          <div className="w-full mt-4 flex gap-2">
            {onCall && (
              <button
                onClick={() => onCall(phoneNumber)}
                className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg py-2 px-3 text-xs font-semibold transition-all shadow-sm shadow-indigo-600/20"
              >
                <Phone className="w-3.5 h-3.5" />
                {t('contacts.profile.callButton', 'Appeler')}
              </button>
            )}
            {onOpenConversation && (
              <button
                onClick={onOpenConversation}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 rounded-lg py-2 px-3 text-xs font-semibold transition-all border',
                  darkMode
                    ? 'border-slate-700 text-slate-200 hover:bg-slate-800'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50',
                )}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                {t('contacts.profile.conversationButton', 'Conversation')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 2. Onglets */}
      <div className={cn('px-3 py-2', darkMode ? 'bg-slate-950/80' : 'bg-white')}>
        <div
          className={cn(
            'flex p-0.5 rounded-lg border',
            darkMode
              ? 'bg-slate-900 border-slate-800'
              : 'bg-gray-50 border-gray-200',
          )}
        >
          {(['info', 'tasks', 'notes'] as TabType[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={cn(
                'flex-1 py-1.5 text-[11px] font-semibold rounded-md transition-all',
                activeTab === tab
                  ? darkMode
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'bg-white text-gray-900 shadow-sm'
                  : darkMode
                    ? 'text-slate-500 hover:text-slate-300'
                    : 'text-gray-400 hover:text-gray-600',
              )}
            >
              {tab === 'info'
                ? t('contacts.profile.tabInfo', 'Info')
                : tab === 'tasks'
                  ? `${t('contacts.profile.tabTasks', 'Tâches')}${pendingTasks.length > 0 ? ` (${pendingTasks.length})` : ''}`
                  : t('contacts.profile.tabNotes', 'Notes')}
            </button>
          ))}
        </div>
      </div>

      {/* 3. Contenu scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {activeTab === 'info' && (
          <div className="px-4 py-3 space-y-4">
            {/* Contact Details */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3
                  className={cn(
                    'text-[10px] font-bold uppercase tracking-[0.1em]',
                    darkMode ? 'text-slate-500' : 'text-gray-400',
                  )}
                >
                  {t('contacts.profile.contactDetails', 'Coordonnées')}
                </h3>
                {suggestions.length >= 2 && (
                  <button
                    type="button"
                    disabled={toutApprouver.isPending}
                    onClick={() =>
                      toutApprouver.mutate(undefined, {
                        onError: () => toast.error(t('sidebar.toasts.error', 'Erreur')),
                      })
                    }
                    className={cn(
                      'flex items-center gap-1 text-[11px] font-medium disabled:opacity-50',
                      darkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700',
                    )}
                  >
                    <CheckIcon className="w-3 h-3" strokeWidth={3} />
                    {t('contacts.suggestions.acceptAll', 'Tout approuver ({{count}})', { count: suggestions.length })}
                  </button>
                )}
              </div>
              <div className="space-y-0.5">
                <InlineField
                  icon={
                    <User
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                  }
                  label={t('contacts.labels.firstName', 'Prénom')}
                  value={contact?.first_name || ''}
                  placeholder={t('contacts.placeholderFirstName', 'Prénom')}
                  darkMode={darkMode}
                  isEditing={editingField === 'first_name'}
                  editValue={editValue}
                  editInputRef={editInputRef}
                  onStartEdit={() =>
                    startEditing('first_name', contact?.first_name || '')
                  }
                  onEditChange={setEditValue}
                  onSave={() => void saveField('first_name', editValue)}
                  onCancel={() => setEditingField(null)}
                  proposition={proposition('first_name')}
                  onKeyDown={(e) => handleEditKeyDown(e, 'first_name')}
                />
                <InlineField
                  icon={
                    <UserCircle
                      className={cn(
                        'w-3.5 h-3.5',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                  }
                  label={t('contacts.labels.lastName', 'Nom')}
                  value={contact?.last_name || ''}
                  placeholder={t('contacts.placeholderLastName', 'Nom')}
                  darkMode={darkMode}
                  isEditing={editingField === 'last_name'}
                  editValue={editValue}
                  editInputRef={editInputRef}
                  onStartEdit={() =>
                    startEditing('last_name', contact?.last_name || '')
                  }
                  onEditChange={setEditValue}
                  onSave={() => void saveField('last_name', editValue)}
                  onCancel={() => setEditingField(null)}
                  proposition={proposition('last_name')}
                  onKeyDown={(e) => handleEditKeyDown(e, 'last_name')}
                />
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <Phone
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                    <span
                      className={cn(
                        'text-[11px]',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    >
                      {t('contacts.labels.phone', 'Téléphone')}
                    </span>
                  </div>
                  <span
                    className={cn(
                      'text-[11px] font-medium',
                      darkMode ? 'text-slate-300' : 'text-gray-700',
                    )}
                  >
                    {formatPhone(phoneNumber)}
                  </span>
                </div>
                <InlineField
                  icon={
                    <Mail
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                  }
                  label={t('contacts.labels.email', 'Email')}
                  value={contact?.email || ''}
                  placeholder={t('contacts.placeholderEmail', 'Email')}
                  darkMode={darkMode}
                  isEditing={editingField === 'email'}
                  editValue={editValue}
                  editInputRef={editInputRef}
                  onStartEdit={() => startEditing('email', contact?.email || '')}
                  onEditChange={setEditValue}
                  onSave={() => void saveField('email', editValue)}
                  onCancel={() => setEditingField(null)}
                  onKeyDown={(e) => handleEditKeyDown(e, 'email')}
                />
                <InlineField
                  icon={
                    <Building
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                  }
                  label={t('contacts.labels.company', 'Entreprise')}
                  value={contact?.company_name || ''}
                  placeholder={t('contacts.placeholderCompany', 'Entreprise')}
                  darkMode={darkMode}
                  isEditing={editingField === 'company_name'}
                  editValue={editValue}
                  editInputRef={editInputRef}
                  onStartEdit={() =>
                    startEditing('company_name', contact?.company_name || '')
                  }
                  onEditChange={setEditValue}
                  onSave={() => void saveField('company_name', editValue)}
                  onCancel={() => setEditingField(null)}
                  proposition={proposition('company_name')}
                  onKeyDown={(e) => handleEditKeyDown(e, 'company_name')}
                />
                <InlineField
                  icon={
                    <Briefcase
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                  }
                  label={t('contacts.labels.jobTitle', 'Poste')}
                  value={contact?.job_title || ''}
                  placeholder={t('contacts.placeholderJobTitle', 'Poste')}
                  darkMode={darkMode}
                  isEditing={editingField === 'job_title'}
                  editValue={editValue}
                  editInputRef={editInputRef}
                  onStartEdit={() =>
                    startEditing('job_title', contact?.job_title || '')
                  }
                  onEditChange={setEditValue}
                  onSave={() => void saveField('job_title', editValue)}
                  onCancel={() => setEditingField(null)}
                  proposition={proposition('job_title')}
                  onKeyDown={(e) => handleEditKeyDown(e, 'job_title')}
                />
                <InlineField
                  icon={
                    <Phone
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                  }
                  label={t('contacts.labels.secondaryPhone', 'Téléphone 2')}
                  value={contact?.secondary_phone ? formatPhone(contact.secondary_phone) : ''}
                  placeholder={t('contacts.placeholderSecondaryPhone', 'Autre numéro')}
                  darkMode={darkMode}
                  isEditing={editingField === 'secondary_phone'}
                  editValue={editValue}
                  editInputRef={editInputRef}
                  onStartEdit={() => startEditing('secondary_phone', contact?.secondary_phone || '')}
                  onEditChange={setEditValue}
                  onSave={() => void saveField('secondary_phone', editValue)}
                  onCancel={() => setEditingField(null)}
                  proposition={proposition('secondary_phone', formatPhone)}
                  onKeyDown={(e) => handleEditKeyDown(e, 'secondary_phone')}
                />
                <InlineField
                  icon={
                    <MapPin
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                  }
                  label={t('contacts.labels.address', 'Adresse')}
                  value={contact?.address || ''}
                  placeholder={t('contacts.placeholderAddress', 'Adresse')}
                  darkMode={darkMode}
                  isEditing={editingField === 'address'}
                  editValue={editValue}
                  editInputRef={editInputRef}
                  onStartEdit={() => startEditing('address', contact?.address || '')}
                  onEditChange={setEditValue}
                  onSave={() => void saveField('address', editValue)}
                  onCancel={() => setEditingField(null)}
                  proposition={proposition('address')}
                  onKeyDown={(e) => handleEditKeyDown(e, 'address')}
                />
                <InlineField
                  icon={
                    <Globe
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                  }
                  label={t('contacts.labels.website', 'Site web')}
                  value={(contact?.website || '').replace(/^https?:\/\//, '')}
                  placeholder={t('contacts.placeholderWebsite', 'Site web')}
                  darkMode={darkMode}
                  isEditing={editingField === 'website'}
                  editValue={editValue}
                  editInputRef={editInputRef}
                  onStartEdit={() => startEditing('website', contact?.website || '')}
                  onEditChange={setEditValue}
                  onSave={() => void saveField('website', editValue)}
                  onCancel={() => setEditingField(null)}
                  proposition={proposition('website', (v) => v.replace(/^https?:\/\//, ''))}
                  onKeyDown={(e) => handleEditKeyDown(e, 'website')}
                />
                <InlineField
                  icon={
                    <Hash
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                  }
                  label={t('contacts.labels.tags', 'Étiquettes')}
                  value={(contact?.tags || []).join(', ')}
                  placeholder={t('contacts.placeholderTags', 'VIP, fournisseur…')}
                  darkMode={darkMode}
                  isEditing={editingField === 'tags'}
                  editValue={editValue}
                  editInputRef={editInputRef}
                  onStartEdit={() => startEditing('tags', (contact?.tags || []).join(', '))}
                  onEditChange={setEditValue}
                  onSave={() => void saveField('tags', editValue)}
                  onCancel={() => setEditingField(null)}
                  onKeyDown={(e) => handleEditKeyDown(e, 'tags')}
                />
                {contact?.source && contact.source !== 'unknown' && (
                  <div className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <Inbox className={cn('w-3 h-3', darkMode ? 'text-slate-500' : 'text-gray-400')} />
                      <span className={cn('text-[11px]', darkMode ? 'text-slate-500' : 'text-gray-400')}>
                        {t('contacts.labels.source', 'Origine')}
                      </span>
                    </div>
                    <span className={cn('text-[11px] font-medium', darkMode ? 'text-slate-300' : 'text-gray-700')}>
                      {t(`contacts.sources.${contact.source}`, contact.source)}
                    </span>
                  </div>
                )}
                <ProprietesContact
                  contact={contact}
                  phoneNumber={phoneNumber}
                  darkMode={darkMode}
                  locale={locale}
                  peutGerer={peutGererProprietes}
                />
                {/* Status */}
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <Tag
                      className={cn(
                        'w-3 h-3',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    />
                    <span
                      className={cn(
                        'text-[11px]',
                        darkMode ? 'text-slate-500' : 'text-gray-400',
                      )}
                    >
                      {t('contacts.labels.status', 'Statut')}
                    </span>
                  </div>
                  <Select
                    value={contact?.status || 'prospect'}
                    onValueChange={(value) =>
                      void updateContact.mutateAsync({
                        status: value as ContactStatus,
                      })
                    }
                  >
                    <SelectTrigger
                      className={cn(
                        'h-auto w-auto border-0 p-0 shadow-none gap-1.5 text-[11px] font-medium focus:ring-0',
                        darkMode ? 'text-slate-300' : 'text-gray-700',
                      )}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent
                      className={cn(
                        'min-w-[140px] rounded-xl',
                        darkMode
                          ? 'bg-slate-900 border-slate-700 text-slate-200'
                          : 'bg-white border-gray-100 shadow-xl text-gray-900',
                      )}
                    >
                      {(Object.entries(statusConfig) as [
                        ContactStatus,
                        { label: string; color: string },
                      ][]).map(([key, cfg]) => (
                        <SelectItem
                          key={key}
                          value={key}
                          className={cn(
                            'text-xs rounded-lg',
                            darkMode
                              ? 'text-slate-300 focus:bg-slate-800 focus:text-white'
                              : 'text-gray-700 focus:bg-gray-50 focus:text-gray-900',
                          )}
                        >
                          {cfg.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Engagement Summary */}
            <div
              className={cn(
                'rounded-xl p-3 border',
                darkMode
                  ? 'bg-slate-900/50 border-slate-800/60'
                  : 'bg-gray-50 border-gray-200',
              )}
            >
              <h3
                className={cn(
                  'text-[10px] font-bold uppercase tracking-[0.1em] mb-3',
                  darkMode ? 'text-slate-500' : 'text-gray-400',
                )}
              >
                {t('contacts.profile.engagement', 'Engagement')}
              </h3>
              <div className="flex justify-between items-center text-center">
                <div className="flex flex-col gap-0.5">
                  <span
                    className={cn(
                      'text-sm font-bold',
                      darkMode ? 'text-white' : 'text-gray-900',
                    )}
                  >
                    {contact?.total_calls || 0}
                  </span>
                  <span
                    className={cn(
                      'text-[9px] uppercase',
                      darkMode ? 'text-slate-500' : 'text-gray-400',
                    )}
                  >
                    {t('contacts.profile.callsLabel', 'Appels')}
                  </span>
                </div>
                <div
                  className={cn(
                    'w-px h-8',
                    darkMode ? 'bg-slate-800' : 'bg-gray-200',
                  )}
                />
                <div className="flex flex-col gap-0.5">
                  <span
                    className={cn(
                      'text-sm font-bold',
                      darkMode ? 'text-white' : 'text-gray-900',
                    )}
                  >
                    {contact?.total_sms || 0}
                  </span>
                  <span
                    className={cn(
                      'text-[9px] uppercase',
                      darkMode ? 'text-slate-500' : 'text-gray-400',
                    )}
                  >
                    {t('contacts.profile.smsLabel', 'SMS')}
                  </span>
                </div>
                <div
                  className={cn(
                    'w-px h-8',
                    darkMode ? 'bg-slate-800' : 'bg-gray-200',
                  )}
                />
                <div className="flex flex-col gap-0.5">
                  <span
                    className={cn(
                      'text-sm font-bold',
                      darkMode ? 'text-white' : 'text-gray-900',
                    )}
                  >
                    {lastInteractionLabel}
                  </span>
                  <span
                    className={cn(
                      'text-[9px] uppercase',
                      darkMode ? 'text-slate-500' : 'text-gray-400',
                    )}
                  >
                    {t('contacts.profile.lastContact', 'Dernier contact')}
                  </span>
                </div>
              </div>
            </div>

            {/* Agent Stats */}
            {contact?.agent_stats && contact.agent_stats.length > 0 && (
              <div>
                <h3
                  className={cn(
                    'text-[10px] font-bold uppercase tracking-[0.1em] mb-3',
                    darkMode ? 'text-slate-500' : 'text-gray-400',
                  )}
                >
                  {t('contacts.profile.previousAgents', 'Agents précédents')}
                </h3>
                <div className="space-y-2">
                  {contact.agent_stats.map((stat, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            'w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold',
                            darkMode
                              ? 'bg-slate-800 text-slate-400'
                              : 'bg-gray-100 text-gray-600',
                          )}
                        >
                          {stat.agent_initials}
                        </div>
                        <span
                          className={cn(
                            'text-xs',
                            darkMode ? 'text-slate-300' : 'text-gray-700',
                          )}
                        >
                          {stat.agent_name}
                        </span>
                      </div>
                      <span
                        className={cn(
                          'text-[10px]',
                          darkMode ? 'text-slate-500' : 'text-gray-400',
                        )}
                      >
                        {stat.call_count} {t('contacts.calls', 'appels')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="px-4 py-3">
            <div className="relative mb-3">
              <input
                type="text"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void handleAddTask()
                  }
                }}
                placeholder={t(
                  'contacts.tasks.placeholder',
                  'Nouvelle tâche...',
                )}
                className={cn(
                  'w-full text-xs px-3 py-2.5 pr-9 rounded-lg border transition-colors focus:outline-none',
                  darkMode
                    ? 'bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 focus:border-slate-600'
                    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-300',
                )}
              />
              {newTaskTitle.trim() && (
                <button
                  onClick={() => void handleAddTask()}
                  disabled={createTask.isPending}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-500 hover:text-indigo-600 disabled:opacity-50"
                >
                  {createTask.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Plus className="w-3.5 h-3.5" />
                  )}
                </button>
              )}
            </div>

            <div className="space-y-1">
              {tasksQuery.isLoading ? (
                <div className="flex justify-center py-3">
                  <Loader2
                    className={cn(
                      'w-4 h-4 animate-spin',
                      darkMode ? 'text-slate-500' : 'text-gray-400',
                    )}
                  />
                </div>
              ) : pendingTasks.length === 0 && completedTasks.length === 0 ? (
                <p
                  className={cn(
                    'text-xs italic',
                    darkMode ? 'text-slate-600' : 'text-gray-400',
                  )}
                >
                  {t('contacts.tasks.empty', 'Aucune tâche')}
                </p>
              ) : (
                <>
                  {pendingTasks.map((task: CallTask) => (
                    <div
                      key={task.id}
                      className={cn(
                        'group flex items-center gap-2 py-1.5 px-1 rounded-md transition-colors',
                        darkMode ? 'hover:bg-slate-900/50' : 'hover:bg-gray-50',
                      )}
                    >
                      <button
                        onClick={() => toggleTask.mutate(task)}
                        className={cn(
                          'w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 transition-colors',
                          darkMode
                            ? 'border-slate-600 hover:border-indigo-400'
                            : 'border-gray-300 hover:border-indigo-500',
                        )}
                      />
                      <span
                        className={cn(
                          'flex-1 text-xs truncate',
                          darkMode ? 'text-slate-300' : 'text-gray-700',
                        )}
                      >
                        {task.title}
                      </span>
                      <button
                        onClick={() => deleteTask.mutate(task.id)}
                        className={cn(
                          'opacity-0 group-hover:opacity-100 p-0.5 rounded transition-all',
                          darkMode
                            ? 'text-slate-600 hover:text-red-400'
                            : 'text-gray-300 hover:text-red-500',
                        )}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {completedTasks.length > 0 && (
                    <div className="mt-2">
                      <button
                        onClick={() => setShowCompletedTasks((p) => !p)}
                        className={cn(
                          'flex items-center gap-1 text-[10px]',
                          darkMode
                            ? 'text-slate-500 hover:text-slate-400'
                            : 'text-gray-400 hover:text-gray-500',
                        )}
                      >
                        {showCompletedTasks ? (
                          <ChevronUp className="w-3 h-3" />
                        ) : (
                          <ChevronDown className="w-3 h-3" />
                        )}
                        {completedTasks.length}{' '}
                        {t('contacts.tasks.completed', 'terminées')}
                      </button>
                      {showCompletedTasks && (
                        <div className="mt-1 space-y-1">
                          {completedTasks.map((task: CallTask) => (
                            <div
                              key={task.id}
                              className={cn(
                                'group flex items-center gap-2 py-1 px-1 rounded-md transition-colors',
                                darkMode
                                  ? 'hover:bg-slate-900/50'
                                  : 'hover:bg-gray-50',
                              )}
                            >
                              <button
                                onClick={() => toggleTask.mutate(task)}
                                className={cn(
                                  'w-3.5 h-3.5 rounded-full flex-shrink-0 flex items-center justify-center',
                                  darkMode
                                    ? 'bg-emerald-500/20 text-emerald-400'
                                    : 'bg-emerald-100 text-emerald-600',
                                )}
                              >
                                <CheckIcon className="w-2 h-2" />
                              </button>
                              <span
                                className={cn(
                                  'flex-1 text-xs truncate line-through',
                                  darkMode ? 'text-slate-500' : 'text-gray-400',
                                )}
                              >
                                {task.title}
                              </span>
                              <button
                                onClick={() => deleteTask.mutate(task.id)}
                                className={cn(
                                  'opacity-0 group-hover:opacity-100 p-0.5 rounded transition-all',
                                  darkMode
                                    ? 'text-slate-600 hover:text-red-400'
                                    : 'text-gray-300 hover:text-red-500',
                                )}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="px-4 py-3">
            <div className="relative mb-3">
              <textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                onKeyDown={handleNoteKeyDown}
                placeholder={t(
                  'contacts.placeholders.note',
                  'Écrire une note...',
                )}
                rows={3}
                className={cn(
                  'w-full text-xs px-3 py-2.5 rounded-lg border transition-colors resize-none focus:outline-none',
                  darkMode
                    ? 'bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 focus:border-slate-600'
                    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-300',
                )}
              />
              {noteContent.trim() && (
                <button
                  onClick={() => void handleAddNote()}
                  disabled={addNote.isPending}
                  className="absolute right-2 bottom-2.5 text-indigo-500 hover:text-indigo-600 disabled:opacity-50"
                >
                  {addNote.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                </button>
              )}
            </div>
            <div className="space-y-3">
              {contact?.recent_notes && contact.recent_notes.length > 0 ? (
                contact.recent_notes.map((note) => (
                  <div
                    key={note.id}
                    className={cn(
                      'p-2.5 rounded-lg border',
                      darkMode
                        ? 'bg-slate-800/30 border-slate-700/30'
                        : 'bg-gray-50 border-gray-100',
                    )}
                  >
                    <div className="flex justify-between mb-1">
                      <span
                        className={cn(
                          'text-[9px] font-bold',
                          darkMode ? 'text-slate-400' : 'text-gray-500',
                        )}
                      >
                        {note.author_name} ·{' '}
                        {format(new Date(note.created_at), 'd MMM HH:mm', {
                          locale: dateLocale,
                        })}
                      </span>
                    </div>
                    <p
                      className={cn(
                        'text-[11px] leading-relaxed',
                        darkMode ? 'text-slate-300' : 'text-gray-700',
                      )}
                    >
                      {note.content}
                    </p>
                  </div>
                ))
              ) : (
                <p
                  className={cn(
                    'text-xs italic',
                    darkMode ? 'text-slate-600' : 'text-gray-400',
                  )}
                >
                  {t('contacts.noNotes', 'Aucune note')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Archive / Restore / Delete */}
      {contact?.id && (
        <div
          className={cn(
            'px-3 py-3 border-t flex-shrink-0',
            darkMode ? 'border-slate-800' : 'border-gray-100',
          )}
        >
          {showDeleteConfirm ? (
            <div className="space-y-2">
              <p
                className={cn(
                  'text-[11px] text-center',
                  darkMode ? 'text-slate-400' : 'text-gray-500',
                )}
              >
                {t(
                  'contacts.delete.confirmMessage',
                  'Supprimer ce contact ? Il va dans la corbeille 30 jours. Ses appels et textos restent dans l’historique.',
                )}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className={cn(
                    'flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    darkMode
                      ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                >
                  {t('contacts.archive.cancel', 'Annuler')}
                </button>
                <button
                  onClick={() => void handleDelete()}
                  disabled={supprimer.isPending}
                  className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {t('contacts.delete.confirm', 'Supprimer')}
                </button>
              </div>
            </div>
          ) : showArchiveConfirm ? (
            <div className="space-y-2">
              <p
                className={cn(
                  'text-[11px] text-center',
                  darkMode ? 'text-slate-400' : 'text-gray-500',
                )}
              >
                {t(
                  'contacts.archive.confirmMessage',
                  'Archiver ce contact ?',
                )}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowArchiveConfirm(false)}
                  className={cn(
                    'flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    darkMode
                      ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                >
                  {t('contacts.archive.cancel', 'Annuler')}
                </button>
                <button
                  onClick={() => void handleArchive()}
                  disabled={archive.isPending}
                  className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
                >
                  {t('contacts.archive.confirm', 'Archiver')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              {contact.is_archived ? (
                <button
                  onClick={() => void handleRestore()}
                  disabled={restore.isPending}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-50',
                    darkMode
                      ? 'bg-emerald-900/30 text-emerald-400 hover:bg-emerald-900/50'
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                  )}
                >
                  <ArchiveRestore className="w-3.5 h-3.5" />
                  {t('contacts.restore.button', 'Restaurer')}
                </button>
              ) : (
                <button
                  onClick={() => setShowArchiveConfirm(true)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors',
                    darkMode
                      ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                >
                  <Archive className="w-3.5 h-3.5" />
                  {t('contacts.archive.button', 'Archiver')}
                </button>
              )}
              {peutGererProprietes && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors',
                    darkMode
                      ? 'bg-red-900/20 text-red-400 hover:bg-red-900/40'
                      : 'bg-red-50 text-red-600 hover:bg-red-100',
                  )}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('contacts.delete.button', 'Supprimer')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface InlineFieldProps {
  icon: React.ReactNode
  label: string
  value: string
  placeholder: string
  darkMode: boolean
  isEditing: boolean
  editValue: string
  editInputRef: React.RefObject<HTMLInputElement | null>
  onStartEdit: () => void
  onEditChange: (v: string) => void
  onSave: () => void
  onCancel: () => void
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void
  /** Valeur proposée par l'IA, affichée à la place de l'invite si le champ est vide. */
  proposition?: React.ReactNode
}

function InlineField({
  icon,
  label,
  value,
  placeholder,
  darkMode,
  isEditing,
  editValue,
  editInputRef,
  onStartEdit,
  onEditChange,
  onSave,
  onKeyDown,
  proposition,
}: InlineFieldProps) {
  if (isEditing) {
    return (
      <div className="flex items-center justify-between py-1.5 px-0">
        <div className="flex items-center gap-2 flex-shrink-0">
          {icon}
          <span
            className={cn(
              'text-[11px]',
              darkMode ? 'text-slate-500' : 'text-gray-400',
            )}
          >
            {label}
          </span>
        </div>
        <input
          ref={editInputRef}
          value={editValue}
          onChange={(e) => onEditChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onSave}
          placeholder={placeholder}
          className={cn(
            'max-w-[160px] text-[11px] font-medium text-right bg-transparent border-0 border-b outline-none py-0 px-0 transition-colors',
            darkMode
              ? 'text-white border-indigo-500/50 placeholder:text-slate-600'
              : 'text-gray-900 border-indigo-400/50 placeholder:text-gray-300',
          )}
        />
      </div>
    )
  }
  return (
    <div
      className={cn(
        'flex items-center justify-between py-1.5 cursor-pointer group rounded-md px-0 transition-colors',
        darkMode ? 'hover:bg-slate-900/60' : 'hover:bg-gray-50/80',
      )}
      onClick={onStartEdit}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span
          className={cn(
            'text-[11px]',
            darkMode ? 'text-slate-500' : 'text-gray-400',
          )}
        >
          {label}
        </span>
      </div>
      {!value && proposition ? (
        proposition
      ) : (
        <span
          className={cn(
            'text-[11px] font-medium truncate max-w-[160px]',
            value
              ? darkMode
                ? 'text-slate-300'
                : 'text-gray-700'
              : darkMode
                ? 'text-slate-600 italic'
                : 'text-gray-300 italic',
          )}
        >
          {value || placeholder}
        </span>
      )}
    </div>
  )
}
